/**
 * Tenant-switcher core (the proper multi-tenant switcher — apps/ops + apps/admin).
 *
 * A Mangrove cofounder (or any multi-tenant user) belongs to several tenants
 * (Mangrove HQ + Fancy Fork + a cosmetics trial + …). `switchTenant` changes
 * which tenant they operate as. Two paths, decided by the caller:
 *
 *  - SAME surface (the target renders in the app you're already in): in-place
 *    rebind — cancel in-flight queries → activate the tenant server-side → wipe
 *    BOTH cache layers (IndexedDB via `persister.removeClient` + in-memory via
 *    `queryClient.clear`) → invalidate so every query cold-fetches the new
 *    tenant. No navigation (best UX).
 *  - CROSS surface (e.g. a cofounder in the ops console switching to a
 *    restaurant/general tenant whose data lives in apps/admin): activate, then
 *    `onCrossSurface()` — a hard navigation to the target app, which cold-boots
 *    its own (separate) IndexedDB cache against the new tenant.
 *
 * Ordering is load-bearing: `cancelQueries` first (stop in-flight reads
 * resolving into the new scope); `removeClient` before `clear` (so a persist
 * tick can't re-write stale data); `invalidateQueries` last. Paused offline
 * mutations were issued under the OLD tenant and are intentionally dropped by
 * the wipe — do NOT resume them (a stranded mutation in the wrong business is
 * worse than a lost one; the user re-issues).
 *
 * Decoupled by design: no tRPC client, no router contract, no Surface type —
 * the caller injects `activate` (`trpc.tenant.activate.mutateAsync`), the
 * `queryClient`, the `persister`, and the cross-surface decision + navigation.
 * Unit-testable with plain fakes.
 */
import type { QueryClient } from '@tanstack/react-query';
import type { Persister } from '@tanstack/react-query-persist-client';

export interface SwitchTenantOptions {
  /** Target tenant id (rebound server-side via `activate`). */
  tenantId: string;
  /** True when the target tenant renders in a DIFFERENT app-surface than the current one. */
  isCrossSurface: boolean;
  /** Rebinds `sessions.tenant_id` — typically `() => trpc.tenant.activate.mutateAsync({ tenantId })`. */
  activate: () => Promise<unknown>;
  /** The app's TanStack QueryClient. */
  queryClient: QueryClient;
  /** The app's offline persister (IndexedDB). `null`/omitted if persistence isn't configured. */
  persister?: Pick<Persister, 'removeClient'> | null;
  /** Cross-surface only: navigate to the target app AFTER activate (e.g. `window.location.assign(surfaceUrl(target))`). */
  onCrossSurface: () => void;
}

/**
 * Wipe BOTH cache layers — in-memory (`queryClient`) + persisted (IndexedDB via
 * `persister.removeClient`). The identity-change counterpart to `switchTenant`:
 * call it whenever the *user* (not just the tenant) changes — on logout, and
 * before a different user signs in on the same device. Without it, the admin/ops
 * persisted query cache survives a logout, so the next user briefly sees the
 * previous account's data (tenant list, modules, branding) — and, on a shared
 * device, even Mangrove HQ chrome cached from a staff login (ADR-0085). The
 * server is never fooled (every query re-authorizes), but the UI must not show
 * another account's data.
 *
 * Same load-bearing order as `switchTenant`: cancel in-flight reads →
 * `removeClient` (IndexedDB) before `clear` (in-memory) so a persist tick can't
 * re-write stale data between the two. Does NOT invalidate/refetch — the caller
 * is logging out / navigating to login, so a cold next boot is desired.
 */
export async function clearClientCache(opts: {
  queryClient: QueryClient;
  persister?: Pick<Persister, 'removeClient'> | null;
}): Promise<void> {
  await opts.queryClient.cancelQueries();
  if (opts.persister) {
    await opts.persister.removeClient();
  }
  opts.queryClient.clear();
}

export async function switchTenant(opts: SwitchTenantOptions): Promise<void> {
  // Stop in-flight reads so nothing resolves into the new tenant scope.
  await opts.queryClient.cancelQueries();
  // Rebind the session server-side. NOTE: the new binding is picked up on the
  // NEXT request — everything after this must be a fresh fetch, never a cache read.
  await opts.activate();

  if (opts.isCrossSurface) {
    // The destination app cold-boots its own cache; no local wipe needed.
    opts.onCrossSurface();
    return;
  }

  // Same surface: wipe both cache layers, then cold-fetch the new tenant.
  // removeClient (IndexedDB) BEFORE clear (in-memory) so a persist tick can't
  // re-write stale data between the two.
  if (opts.persister) {
    await opts.persister.removeClient();
  }
  opts.queryClient.clear();
  await opts.queryClient.invalidateQueries();
}

/** The two app-surfaces a switchable tenant can render in. */
export type TenantAppSurface = 'app' | 'admin';

/**
 * Which app-surface a tenant of this `store_type` renders in. Mangrove HQ
 * (`saas_company`) lives in the ops console ('app'); every customer vertical
 * (restaurant / grocery / general / driving_school / …) lives in the tenant
 * admin ('admin'). Vertical-agnostic: a new vertical needs NO change here
 * (anything that isn't `saas_company` is a tenant surface).
 */
export function tenantSurface(storeType: string): TenantAppSurface {
  return storeType === 'saas_company' ? 'app' : 'admin';
}

/**
 * Human label for a tenant's `store_type`, for the switcher's secondary line
 * ("Restaurant · Manager", "Cosmetics · Owner", "Mangrove HQ · Cofounder").
 * Adding a vertical = one entry here (falls back to the raw value).
 */
const STORE_TYPE_LABELS: Readonly<Record<string, string>> = {
  saas_company: 'Mangrove HQ',
  restaurant: 'Restaurant',
  grocery: 'Grocery',
  convenience: 'Convenience',
  mixed: 'Mixed',
  driving_school: 'Driving School',
  general: 'General',
  mobile_vendor: 'Mobile Vendor',
  contractor: 'Contractor',
  education: 'Education',
};

/**
 * Every value of the `tenant_store_type` Postgres enum, so the test below can
 * assert none is missing a label. A vertical added to the enum without a label
 * here renders its RAW value in the workspace switcher — which is how "QED
 * education · owner" reached production, sitting between correctly-labelled
 * "Fancy Fork Restaurant" and "TMix Roofing Contractor".
 *
 * Keep in sync with the enum; the test is what makes that non-optional.
 */
export const ALL_STORE_TYPES = [
  'restaurant',
  'grocery',
  'convenience',
  'mixed',
  'saas_company',
  'driving_school',
  'general',
  'education',
  'mobile_vendor',
  'contractor',
] as const;

export function verticalLabel(storeType: string): string {
  return STORE_TYPE_LABELS[storeType] ?? storeType;
}
